import { createRoute, dateIsInFuture } from '@backoffice-frontend/common'
import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import {
  FormSelectfield,
  FormTextarea,
  LoadingButton,
  MultiSelectComboField,
  useForm,
} from '@backoffice-frontend/forms'
import {
  type Fleet,
  type InputMaybe,
  WorkOrderOperationalImpact,
  WorkOrderPriority,
  type WorkOrderServiceItem,
  WorkOrderStatus,
} from '@backoffice-frontend/graphql'
import {
  useMoiaErrorNotification,
  useMoiaSuccessNotification,
} from '@backoffice-frontend/patterns'
import { useGlobalRegion } from '@backoffice-frontend/service-area-picker'
import { usePaceValidators } from '@backoffice-frontend/validators'
import {
  Button,
  Callout,
  DatePicker,
  Dialog,
  FormField,
  FormFieldValidation,
  Option,
} from '@moia-dev/pace-web'
import sortBy from 'lodash/sortBy'
import { useId, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useCreateWorkOrderDialogMutation } from '../../graphql/CreateWorkOrder.hook'
import { useWorkOrderStatusTranslation } from '../../hooks/useWorkOrderStatusTranslation'
import {
  usePriorityTranslation,
  useWorkOrderServiceItemTranslation,
} from '../../hooks/useWorkOrderTranslations'
import { workOrderServiceItemOptions } from '../../utils/WorkOrderServiceItemCategories'
import { WorkOrdersComponentsRoutes } from '../../WorkOrdersRoutes'
import { useOperationalVehiclesForStateChangeQuery } from './graphql/OperationalVehiclesForStateChange.hook'
import { useVehiclesNonOperationalPolling } from './useVehiclesNonOperationalPolling'

const STATUS_OPTIONS = [WorkOrderStatus.Open, WorkOrderStatus.InProgress]

const PRIORITY_OPTIONS = [
  WorkOrderPriority.High,
  WorkOrderPriority.Medium,
  WorkOrderPriority.Low,
]

type FormValues = {
  serviceItem: WorkOrderServiceItem | null
  status: WorkOrderStatus
  priority: WorkOrderPriority | null
  dueAt: Date | null
  description: string
}

const workOrdersListUrl = `${createRoute(
  WorkOrdersComponentsRoutes.WorkOrders,
)}?statuses=${STATUS_OPTIONS.join(',')}`

type VehicleInfo = {
  id: string
  label: number | null
  fleet: { name: string } | null
}

const renderVehicleOption = (vehicle: VehicleInfo | null) =>
  `${vehicle?.label} (${vehicle?.fleet?.name})`

export type ChangeVehicleToNonOperationalDialogProps = {
  id: string
  closeDialog: () => void
  selectedVehicle?: VehicleInfo | null
  preSelectedVehicles?: VehicleInfo[]
  filteredFleets: InputMaybe<Fleet['id'][]> | undefined
  onSuccess: () => void
}

export const ChangeVehicleToNonOperationalDialog = ({
  id,
  closeDialog,
  selectedVehicle = null,
  preSelectedVehicles = [],
  filteredFleets,
  onSuccess,
}: ChangeVehicleToNonOperationalDialogProps) => {
  const { t } = useTranslation()
  const statusTranslation = useWorkOrderStatusTranslation()
  const serviceItemTranslation = useWorkOrderServiceItemTranslation()
  const priorityTranslation = usePriorityTranslation()
  const { required } = usePaceValidators()
  const formId = useId()
  const navigate = useNavigate()
  const [regions] = useGlobalRegion()
  const isSingleVehiclePreselected = selectedVehicle !== null
  const [query, setQuery] = useState(
    isSingleVehiclePreselected ? renderVehicleOption(selectedVehicle) : '',
  )
  const [selectedVehicles, setSelectedVehicles] = useState<VehicleInfo[]>(
    selectedVehicle ? [selectedVehicle] : preSelectedVehicles,
  )

  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()

  const { control, handleSubmit, formState, reset, watch } =
    useForm<FormValues>({
      mode: 'onChange',
      defaultValues: {
        serviceItem: null,
        status: WorkOrderStatus.Open,
        priority: WorkOrderPriority.High,
        dueAt: null,
        description: '',
      },
    })

  const dueAtValue = watch('dueAt')
  const dueAtIsInvalid = !!dueAtValue && !dateIsInFuture(dueAtValue)

  const handleClose = () => {
    closeDialog()
    reset()
    setSelectedVehicles(preSelectedVehicles)
    setQuery('')
  }

  const { startPolling, isPolling } = useVehiclesNonOperationalPolling({
    onAllNonOperational: () => {
      onSuccess()
      handleClose()
      enqueueMoiaSuccessNotification(
        // The translation scanner runs with `plural: false`, so the plural form
        // is picked explicitly rather than via i18next's `count`.
        selectedVehicles.length === 1
          ? t(
              'workOrdersComponents_changeVehicleToNonOperationalDialog_success_one',
            )
          : t(
              'workOrdersComponents_changeVehicleToNonOperationalDialog_success_many',
              {
                count: selectedVehicles.length,
              },
            ),
        {
          actionLabel: t(
            'workOrdersComponents_changeVehicleToNonOperationalDialog_viewWorkOrders_action',
          ),
          onAction: () => navigate(workOrdersListUrl),
        },
      )
    },
    onTimeout: () => {
      onSuccess()
      handleClose()
      enqueueMoiaErrorNotification(
        t('workOrdersComponents_changeVehicleToNonOperationalDialog_pending'),
      )
    },
  })

  const { data: vehiclesData, loading: vehiclesAreLoading } =
    useErrorHandlingQuery(useOperationalVehiclesForStateChangeQuery, {
      variables: { regions: regions?.id ?? '', fleets: filteredFleets },
      skip: !regions,
    })

  const selectableVehicles = vehiclesData?.fleetVehicles.vehicles

  const vehicleOptions = selectableVehicles
    ? sortBy(selectableVehicles, 'label').filter(vehicle =>
        vehicle.label?.toString().includes(query),
      )
    : []

  const onSubmit = async (values: FormValues) => {
    if (values.serviceItem == null) return

    const vehicleIds = selectedVehicles.map(vehicle => vehicle.id)

    await createWorkOrder({
      variables: {
        vehicleIDs: vehicleIds,
        status: values.status,
        serviceItem: values.serviceItem,
        priority: values.priority,
        dueAt: values.dueAt?.toISOString(),
        description: values.description,
        assigneeId: null,
        operationalImpact: WorkOrderOperationalImpact.NonOperational,
      },
    })
  }

  const [createWorkOrder] = useErrorHandlingMutation(
    useCreateWorkOrderDialogMutation,
    {
      onCompleted: () => {
        startPolling(selectedVehicles.map(vehicle => vehicle.id))
      },
    },
  )

  const isSubmitDisabled =
    !formState.isValid ||
    vehiclesAreLoading ||
    selectedVehicles.length === 0 ||
    dueAtIsInvalid

  return (
    <Dialog
      css={{ width: 480 }}
      id={id}
      title={t(
        'workOrdersComponents_changeVehicleToNonOperationalDialog_title',
      )}
      onClose={handleClose}
      actions={
        <>
          <Button
            onClick={handleClose}
            label={t('common_cancel_button')}
            variant="secondary"
            size="compact"
            disabled={isPolling}
          />
          <LoadingButton
            form={formId}
            type="submit"
            disabled={isSubmitDisabled || isPolling}
            loading={formState.isSubmitting || isPolling}
            label={t(
              'workOrdersComponents_changeVehicleToNonOperationalDialog_submit_button',
            )}
            size="compact"
          />
        </>
      }
    >
      <form
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space3,
        })}
      >
        <Callout
          variant="warning"
          title={t(
            'workOrdersComponents_changeVehicleToNonOperationalDialog_callout_title',
          )}
          hideDismiss
        >
          {t(
            'workOrdersComponents_changeVehicleToNonOperationalDialog_callout_text',
          )}
        </Callout>
        <MultiSelectComboField
          label={t('common_vehicle_label')}
          required
          disabled={isSingleVehiclePreselected}
          showTags={!isSingleVehiclePreselected}
          query={query}
          onQueryChange={setQuery}
          values={selectedVehicles}
          onChange={setSelectedVehicles}
          options={vehicleOptions}
          size="compact"
          optionToId={vehicle => vehicle.id}
          optionToQuery={vehicle => vehicle?.label?.toString() as string}
          renderOption={renderVehicleOption}
          emptyState={t(
            'workOrdersComponents_changeVehicleToNonOperationalDialog_noOperationalVehicleFound',
          )}
          closeOnSelect
        />
        <FormSelectfield<FormValues, 'serviceItem'>
          name={'serviceItem'}
          size="compact"
          label={t('common_serviceItem_label')}
          control={control}
          options={workOrderServiceItemOptions()}
          renderValue={option =>
            serviceItemTranslation(option as WorkOrderServiceItem)
          }
          rules={{ required }}
        />

        <FormSelectfield<FormValues, 'status'>
          control={control}
          name={'status'}
          size="compact"
          label={t('common_status_label')}
          rules={{ required }}
          options={STATUS_OPTIONS.map(status => (
            <Option key={status} value={status}>
              {statusTranslation(status)}
            </Option>
          ))}
          renderValue={status => statusTranslation(status as WorkOrderStatus)}
        />

        <FormSelectfield<FormValues, 'priority'>
          name={'priority'}
          label={t('common_priority_label')}
          size="compact"
          control={control}
          options={PRIORITY_OPTIONS.map(priority => (
            <Option key={priority} value={priority}>
              {priorityTranslation(priority)}
            </Option>
          ))}
          renderValue={priority =>
            priorityTranslation(priority as WorkOrderPriority)
          }
        />

        <FormField
          label={t('common_dueAt_label')}
          css={{ flex: 1 }}
          validation={
            dueAtIsInvalid ? (
              <FormFieldValidation type="error">
                {t('common_datePicker_dateInFuture_ValidationError')}
              </FormFieldValidation>
            ) : undefined
          }
        >
          <div
            css={theme => ({
              '& input[value=""]': {
                color: theme.semantic.ColorContentDisabled,
              },
            })}
          >
            <Controller
              control={control}
              name="dueAt"
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  error={dueAtIsInvalid}
                  value={value || undefined}
                  onChange={onChange}
                  aria-label={t('common_dueAt_label')}
                  disabledDates={{ before: new Date() }}
                  size="compact"
                />
              )}
            />
          </div>
        </FormField>

        <FormTextarea
          label={t('common_description_label')}
          name="description"
          control={control}
          rows={isSingleVehiclePreselected ? 3 : 2}
        />
      </form>
    </Dialog>
  )
}
